import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "../contexts/TranslationContext";
import { requiredValidation } from "../utils/validation.js";
import { Trash2 } from "lucide-react";

export default function WorkExperienceForm() {
  const { control, register, formState: { errors }, watch, setValue } = useFormContext();
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "experiences"
  });

  const handleCurrentJobChange = (index, isChecked) => {
    if (isChecked) {
      // Desmarcar todos os outros checkboxes
      fields.forEach((_, idx) => {
        if (idx !== index) {
          setValue(`experiences.${idx}.isCurrentJob`, false);
        }
      });
      // Limpar a data de término da experiência atual
      setValue(`experiences.${index}.endDate`, "");
    }
  };


return (
    <div>
      {fields.map((field, index) => {
        const companyError = errors?.experiences?.[index]?.company;
        const roleError = errors?.experiences?.[index]?.role;
        const isCurrentJob = watch(`experiences.${index}.isCurrentJob`);

        return (
          <div key={field.id} className="mb-6 p-6 rounded-lg bg-white/5">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                {t("configureResume.experienceForm.company")}
              </label>
              <input
                type="text"
                placeholder={t("configureResume.experienceForm.companyPlaceholder")}
                className={`w-full p-2 rounded bg-white/10 border text-white placeholder-gray-400 ${
                  companyError ? 'border-red-500' : 'border-gray-600'
                }`}
                {...register(`experiences.${index}.company`, requiredValidation(t, t("configureResume.experienceForm.company")))}
              />
              {companyError && (
                <p className="text-red-500 text-xs mt-1">{companyError.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                {t("configureResume.experienceForm.role")}
              </label>
              <input
                type="text"
                placeholder={t("configureResume.experienceForm.rolePlaceholder")}
                className={`w-full p-2 rounded bg-white/10 border text-white placeholder-gray-400 ${
                  roleError ? 'border-red-500' : 'border-gray-600'
                }`}
                {...register(`experiences.${index}.role`, requiredValidation(t, t("configureResume.experienceForm.role")))}
              />
              {roleError && (
                <p className="text-red-500 text-xs mt-1">{roleError.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("configureResume.experienceForm.startDate")}
                </label>
                <input
                  type="date"
                  className="w-full p-2 rounded bg-white/10 border border-gray-600 text-white focus:border-brand-400 focus:outline-none transition"
                  {...register(`experiences.${index}.startDate`)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("configureResume.experienceForm.endDate")}
                </label>
                <input
                  type="date"
                  className={`w-full p-2 rounded border text-white focus:outline-none transition ${
                    isCurrentJob
                      ? 'bg-gray-800/50 border-gray-700 text-gray-500 cursor-not-allowed opacity-60'
                      : 'bg-white/10 border-gray-600 focus:border-brand-400'
                  }`}
                  disabled={isCurrentJob}
                  {...register(`experiences.${index}.endDate`)}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-600 bg-white/10 text-brand-500 focus:ring-brand-400"
                  {...register(`experiences.${index}.isCurrentJob`)}
                  onChange={(e) => {
                    register(`experiences.${index}.isCurrentJob`).onChange(e);
                    handleCurrentJobChange(index, e.target.checked);
                  }}
                />
                {t("configureResume.experienceForm.isCurrentJob")}
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t("configureResume.experienceForm.description")}
              </label>
              <textarea
                placeholder={t("configureResume.experienceForm.descriptionPlaceholder")}
                className="w-full p-2 rounded bg-white/10 border border-gray-600 text-white placeholder-gray-400 focus:border-brand-400 focus:outline-none transition"
                rows={4}
                {...register(`experiences.${index}.descricao`)}
              />
            </div>

            {fields.length > 1 && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 hover:border-red-500/40 transition-all duration-200 group"
                  title={t("configureResume.experienceForm.remove") || "Remover experiência"}
                >
                  <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">
                    {t("configureResume.experienceForm.remove") || "Remover"}
                  </span>
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => append({ company: "", role: "", descricao: "", startDate: "", endDate: "", isCurrentJob: false })}
        className="text-sm text-brand-500 hover:text-brand-400 underline transition"
      >
        + {t("configureResume.experienceForm.add")}
      </button>
    </div>
  );
}
